import { Component, Input, ViewChild, ElementRef } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { EmailTemplateService } from '../../services/email-template.service';
import { EmailTemplate, EmailBranding } from '../../app.model';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { CATEGORIES, CategoryDef, DataBlock, metaFor, PREVIEW_SAMPLE } from '../../email-templates/email-template-meta';

type FieldKey = 'subject' | 'greeting' | 'body_text';

@Component({
  selector: 'app-edit-email-template',
  templateUrl: './edit-email-template.component.html',
  styleUrls: ['./edit-email-template.component.css']
})
export class EditEmailTemplateComponent {

  // NgbModal runs ngOnInit BEFORE the caller assigns componentInstance.template,
  // so initialize from the setter (which fires on assignment) instead.
  private _template!: EmailTemplate;
  @Input() set template(value: EmailTemplate) {
    this._template = value;
    if (value) this.initFromTemplate();
  }
  get template(): EmailTemplate { return this._template; }

  @Input() branding: EmailBranding = {
    primary_color: '#0566EA',
    background_color: '#FFFFFF',
    button_color: '#0566EA',
  };
  @Input() agencyName = ''; // real agency name from the parent; falls back to sample

  @ViewChild('subjectInput') subjectInput!: ElementRef<HTMLInputElement>;
  @ViewChild('greetingInput') greetingInput!: ElementRef<HTMLInputElement>;
  @ViewChild('bodyInput') bodyInput!: ElementRef<HTMLTextAreaElement>;

  saving = false;
  saveError = '';

  // Preview-only metadata
  cat!: CategoryDef;
  cta = '';
  dataBlock: DataBlock | null = null;
  footerText = 'Sent from Clug'; // Phase 2: branding.footer_text

  /** Real agency name, falling back to the sample if not provided. */
  get sampleAgency(): string { return this.agencyName || PREVIEW_SAMPLE['agency_name']; }
  get sampleAgencyMark(): string { return this.initialsOf(this.sampleAgency); }
  /** Sample data for preview substitution, with the real agency name injected. */
  private get sample(): { [key: string]: string } {
    return { ...PREVIEW_SAMPLE, agency_name: this.sampleAgency };
  }

  // Live preview html
  greetingHtml: SafeHtml = '';
  bodyHtml: SafeHtml = '';
  dbTitle = '';
  dbSub = '';

  private initial = { subject: '', greeting: '', body_text: '' };
  private activeField: FieldKey = 'body_text';

  constructor(
    private activeModal: NgbActiveModal,
    private emailTemplateService: EmailTemplateService,
    private sanitizer: DomSanitizer,
  ) { }

  private initFromTemplate() {
    const meta = metaFor(this._template.name);
    this.cat = CATEGORIES[meta.category];
    this.cta = meta.cta;
    this.dataBlock = meta.dataBlock;

    this.initial = {
      subject: this._template.subject || '',
      greeting: this._template.greeting || '',
      body_text: this._template.body_text || '',
    };
    this.renderPreview();
  }

  get categorySub(): string {
    const role: { [k: string]: string } = {
      timesheet: 'agency admin and contractor',
      invoice: 'client',
      account: 'the recipient',
      job: 'contractor and admin',
      onboarding: 'contractor and admin',
      other: 'the recipient',
    };
    return role[metaFor(this.template.name).category] || 'the recipient';
  }

  get bgSoft(): string {
    return (this.branding.primary_color || '#0566EA') + '1A'; // ~10% alpha
  }

  get dirty(): boolean {
    return this.template.subject !== this.initial.subject
      || this.template.greeting !== this.initial.greeting
      || this.template.body_text !== this.initial.body_text;
  }

  // ---------- Field interaction ----------
  setActiveField(field: FieldKey) {
    this.activeField = field;
  }

  onFieldChange() {
    this.saveError = '';
    this.renderPreview();
  }

  insertPlaceholder(placeholder: string) {
    const tag = `{${placeholder}}`;
    const refMap = {
      subject: this.subjectInput,
      greeting: this.greetingInput,
      body_text: this.bodyInput,
    };
    const field = this.activeField;
    const el = refMap[field]?.nativeElement;
    if (!el) return;

    const start = el.selectionStart ?? (this.template[field] || '').length;
    const end = el.selectionEnd ?? start;
    const current = this.template[field] || '';
    this.template[field] = current.slice(0, start) + tag + current.slice(end);
    this.renderPreview();

    // Restore caret after ngModel writes the new value back to the element.
    setTimeout(() => {
      el.focus();
      const pos = start + tag.length;
      el.setSelectionRange(pos, pos);
    });
  }

  // ---------- Preview rendering ----------
  private renderPreview() {
    this.greetingHtml = this.sanitizer.bypassSecurityTrustHtml(
      this.applyPlaceholders(this.template.greeting || '', true));
    this.bodyHtml = this.sanitizer.bypassSecurityTrustHtml(
      this.paragraphsHtml(this.template.body_text || ''));
    if (this.dataBlock) {
      this.dbTitle = this.applyPlaceholders(this.dataBlock.title, false);
      this.dbSub = this.applyPlaceholders(this.dataBlock.sub, false);
    }
  }

  private escapeHtml(s: string): string {
    return String(s).replace(/[&<>"']/g, c =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
  }

  private applyPlaceholders(text: string, highlight: boolean): string {
    const sample = this.sample;
    return this.escapeHtml(text).replace(/\{(\w+)\}/g, (m, key) => {
      const v = sample[key];
      if (v === undefined) return m;
      const safe = this.escapeHtml(v);
      return highlight ? `<span class="tag">${safe}</span>` : safe;
    });
  }

  private paragraphsHtml(text: string): string {
    const sample = this.sample;
    // Escape, substitute placeholders, then turn every newline into <br/> so blank
    // rows render as real blank lines — matching the backend's nl2br(e()) rendering.
    const subbed = this.escapeHtml(text).replace(/\{(\w+)\}/g, (m, key) => {
      const v = sample[key];
      return v === undefined ? m : `<span class="tag">${this.escapeHtml(v)}</span>`;
    });
    return subbed.replace(/\n/g, '<br/>');
  }

  private initialsOf(s: string): string {
    return s.split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();
  }

  // ---------- Actions ----------
  save() {
    this.saving = true;
    this.saveError = '';
    const payload = {
      subject: this.template.subject,
      greeting: this.template.greeting,
      body_text: this.template.body_text,
      content: this.template.body_text, // legacy backend column still required on update
    };
    this.emailTemplateService.updateTemplate(this.template.uuid, payload).subscribe((res: any) => {
      this.saving = false;
      // Backend may return the refreshed template under `data`; fall back to the
      // locally edited template so the list still reflects the save.
      this.activeModal.close(res?.data ?? this.template);
    }, (err: any) => {
      this.saving = false;
      this.saveError = err?.error?.message || 'Could not save. Please try again.';
    });
  }

  resetToDefault() {
    this.emailTemplateService.resetTemplate(this.template.uuid).subscribe((res: any) => {
      const updated = res.data as EmailTemplate;
      this.template.subject = updated.subject;
      this.template.greeting = updated.greeting;
      this.template.body_text = updated.body_text;
      this.initial = {
        subject: updated.subject || '',
        greeting: updated.greeting || '',
        body_text: updated.body_text || '',
      };
      this.renderPreview();
    });
  }

  onClose() {
    if (this.dirty && !window.confirm('Discard unsaved changes?')) return;
    this.activeModal.dismiss();
  }
}
